import { isWithinHours } from '@ui/lib/date'

import {
  CashAdvanceSubscriptionStatus,
  SubscriptionProductEnum,
  type SubscriptionFeeFragmentFragment,
  type SubscriptionFragmentFragment,
} from '@graphql/react-apollo'

import type { AttentionIssue } from '@web-app/components/account/attention/types'

export const MANAGE_ACCOUNT_ROUTE = '/dashboard/account/manage'
export const MEMBERSHIP_ROUTE = '/dashboard/account/manage/membership'

const STATUS_LABEL: Record<CashAdvanceSubscriptionStatus, string> = {
  [CashAdvanceSubscriptionStatus.Active]: 'Active',
  [CashAdvanceSubscriptionStatus.Inactive]: 'Paused',
  [CashAdvanceSubscriptionStatus.PastDue]: 'Past due',
  [CashAdvanceSubscriptionStatus.Trial]: 'Trial',
  [CashAdvanceSubscriptionStatus.Cancelled]: 'Closed',
}

// A trial is cancelled through the pause mutation, so its row reads INACTIVE
// like a paused membership. The product tells them apart: a cancelled trial
// can't be resumed (the server refuses), it just runs out.
function isCancelledTrial(subscription: SubscriptionFragmentFragment) {
  return (
    subscription.status === CashAdvanceSubscriptionStatus.Inactive &&
    subscription.productKey === SubscriptionProductEnum.PersonalFinanceTrial
  )
}

export function membershipLabel(
  subscription: SubscriptionFragmentFragment,
): string {
  return isCancelledTrial(subscription)
    ? 'Trial cancelled'
    : STATUS_LABEL[subscription.status]
}

// The single management action available for a membership, derived from its
// status. pause/resume/payOutstandingFee map 1:1 to the scaffolded mutations;
// cancelTrial is the pause mutation on a trial row, which stops its conversion
// to a paid membership. 'none' covers states with nothing to do: a cancelled
// trial runs out, and a closed membership is restarted from the cash-advance
// surface, not managed here.
export type MembershipAction =
  | 'pause'
  | 'resume'
  | 'payOutstandingFee'
  | 'cancelTrial'
  | 'none'

export interface MembershipFee {
  amount: number
  // The undiscounted fee; above `amount` when a keep-access discount applies.
  baseAmount: number
  // ISO date the next platform fee settles (custom scalar, treated as a string).
  nextFeeChargedAt: string
}

export interface OutstandingFee {
  // Every unpaid fee the server will retry now, summed.
  amount: number
  // A payment was attempted within the last day. Both apps hold the manual
  // retry until it settles, so the pay action is disabled while this is true.
  recentlyAttempted: boolean
}

export interface MembershipState {
  status: CashAdvanceSubscriptionStatus
  label: string
  action: MembershipAction
  // Trials only. While the trial runs, the date it converts to a paid
  // membership; once cancelled, the date access ends.
  trialEndsAt: string | null
  // Only present while the membership is active (the only state that shows the
  // recurring fee row, mirroring iOS).
  fee: MembershipFee | null
  // PAST_DUE only. null while past due means the server is hiding the fee
  // (created or attempted within the last hour): "still processing", not
  // "nothing owed".
  outstanding: OutstandingFee | null
}

const RETRY_HOLD_HOURS = 24

// PAST_DUE never clears on its own; a settled fee is the only way back.
export function membershipIssues(
  subscription: SubscriptionFragmentFragment | null,
): AttentionIssue[] {
  if (subscription?.status !== CashAdvanceSubscriptionStatus.PastDue) return []
  return [
    {
      kind: 'membershipPastDue',
      domain: 'membership',
      severity: 'critical',
      title: 'Membership past due',
      body: 'Your platform fee didn’t go through. Pay it to keep ATM Instant Cash™ available.',
      action: {
        type: 'link',
        label: 'Pay outstanding fee',
        href: MANAGE_ACCOUNT_ROUTE,
      },
    },
  ]
}

// Pure resolver: given the user's membership (and, when past due, the fees the
// server is willing to retry), decide what the membership page can show + do.
export function computeMembershipState({
  subscription,
  outstandingFees = [],
  now = new Date(),
}: {
  subscription: SubscriptionFragmentFragment
  outstandingFees?: SubscriptionFeeFragmentFragment[]
  now?: Date
}): MembershipState {
  const { status, nextCalculatedFee, nextFeeChargedAt, inactiveAt } =
    subscription
  const base = {
    status,
    label: membershipLabel(subscription),
    trialEndsAt: null,
    fee: null,
    outstanding: null,
  }

  switch (status) {
    case CashAdvanceSubscriptionStatus.Active:
      return {
        ...base,
        action: 'pause',
        fee: {
          amount: nextCalculatedFee.amount,
          baseAmount: nextCalculatedFee.baseAmount,
          nextFeeChargedAt,
        },
      }
    case CashAdvanceSubscriptionStatus.Inactive:
      return isCancelledTrial(subscription)
        ? {
            ...base,
            action: 'none',
            // The pause wrote the end date; the fee date is the same instant.
            trialEndsAt: inactiveAt ?? nextFeeChargedAt,
          }
        : { ...base, action: 'resume' }
    case CashAdvanceSubscriptionStatus.Trial:
      return { ...base, action: 'cancelTrial', trialEndsAt: nextFeeChargedAt }
    case CashAdvanceSubscriptionStatus.PastDue:
      return {
        ...base,
        action: 'payOutstandingFee',
        outstanding: outstandingFees.length
          ? {
              amount: outstandingFees.reduce((sum, fee) => sum + fee.amount, 0),
              recentlyAttempted: outstandingFees.some(
                (fee) =>
                  fee.lastPaymentAttemptAt != null &&
                  isWithinHours({
                    value: fee.lastPaymentAttemptAt,
                    hours: RETRY_HOLD_HOURS,
                    now,
                  }),
              ),
            }
          : null,
      }
    case CashAdvanceSubscriptionStatus.Cancelled:
      return { ...base, action: 'none' }
  }
}
